import React, {useCallback, useEffect, useRef, useState} from 'react'

import type {ChatModelOption} from '../ai-chat/BedrockSocketProvider'

interface OmicsComposerModelPickerProps {
  models: ChatModelOption[]
  selectedModelId: string | null
  onSelect: (id: string) => void
}

/** Compact model picker for the in-card composer toolbar (assistant-ui composer action row). */
export const OmicsComposerModelPicker: React.FC<OmicsComposerModelPickerProps> = ({
  models,
  selectedModelId,
  onSelect,
}) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const selectedLabel =
    models.find((m) => m.id === selectedModelId)?.label ?? selectedModelId ?? 'Default'

  useEffect(() => {
    if (!open) return undefined
    const onDocMouseDown = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onDocMouseDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const select = useCallback(
    (id: string) => {
      onSelect(id)
      setOpen(false)
    },
    [onSelect],
  )

  if (models.length === 0) return null

  return (
    <div className='aui-composer-model' ref={rootRef}>
      <button
        type='button'
        className={`aui-composer-model__trigger${open ? ' open' : ''}`}
        data-testid='omics-ai-chat-model-toggle'
        title='Select model'
        aria-haspopup='listbox'
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <span className='aui-composer-model__label'>{selectedLabel}</span>
        <i className={`fas fa-chevron-${open ? 'up' : 'down'}`} aria-hidden />
      </button>
      {open ? (
        <div className='aui-composer-model__menu' data-testid='omics-ai-chat-model-menu' role='listbox'>
          {models.map((m) => (
            <button
              key={m.id}
              type='button'
              role='option'
              aria-selected={m.id === selectedModelId}
              data-testid='omics-ai-chat-model-option'
              className={`aui-composer-model__option${m.id === selectedModelId ? ' active' : ''}`}
              onClick={() => select(m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
